import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Typography,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AccountBase } from '@/models/AccountViewModel';
import StyledButton from '@/components/common/styledButton';
import { useLocalization } from '@/hooks/useLocalization';
import { StyledTabs, StyledTab } from '@/components/common/styledTabs';
import InformationTab, { InformationTabHandle } from './tabs/InformationTab';
import AssetAllocationTab from './tabs/AssetAllocationTab';
import { AssetAllocationTabHandle } from './tabs/AssetAllocationTab';
import HistoricalValuesTab, {
  HistoricalValuesTabHandle,
} from './tabs/HistoricalValuesTab';
import {
  accountsApi,
  useUpdateOfflineAccountMutation,
} from '@/store/accountsApi';
import { diversificationsApi } from '@/store/diversificationsApi';
import type { OfflineAccount } from '@/store/accountsApi';
import { useDispatch } from 'react-redux';

interface EditAccountModalProps {
  open: boolean;
  onClose: () => void;
  account: AccountBase;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const tabIndex = String(index);

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`tabpanel-${tabIndex}`}
      aria-labelledby={`tab-${tabIndex}`}
      aria-hidden={value !== index}
      {...other}
    >
      {/* Always render children to preserve form state when switching tabs.
          Unmounting/recreating caused input values to reset on each parent re-render. */}
      <Box sx={{ pt: 3 }}>{children}</Box>
    </div>
  );
}

const EditAccountModal: React.FC<EditAccountModalProps> = ({
  open,
  onClose,
  account,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLocalization();
  const [tabValue, setTabValue] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const infoRef = React.useRef<InformationTabHandle>(null);
  const assetRef = React.useRef<AssetAllocationTabHandle>(null);
  const historicalValuesRef = React.useRef<HistoricalValuesTabHandle>(null);
  const [updateOfflineAccount] = useUpdateOfflineAccountMutation();
  const dispatch = useDispatch();

  // --------------------
  // Validation state tracking for each tab
  // --------------------
  const [isInformationValid, setInformationValid] = useState<boolean>(true);
  const [isAssetAllocationValid, setAssetAllocationValid] =
    useState<boolean>(true);
  const [isHistoricalValid, setHistoricalValid] = useState<boolean>(true);

  // Track if historical values have been modified (setter passed to tab; value not used here)
  const [_hasHistoricalChanges, setHistoricalChanges] = useState(false);

  const hasUnsavedChanges = useCallback((): boolean => {
    return (
      (infoRef.current?.hasChanges() ?? false) ||
      (assetRef.current?.hasChanges() ?? false) ||
      (historicalValuesRef.current?.hasChanges() ?? false)
    );
  }, []);

  const tabs = useMemo(() => {
    const informationTab = (
      <InformationTab
        key={account.AccountId}
        ref={infoRef}
        account={account}
        modalOpen={open}
        onClose={onClose}
        onValidationChange={setInformationValid}
      />
    );
    const assetAllocationTab = (
      <AssetAllocationTab
        key={account.AccountId}
        ref={assetRef}
        account={account}
        onValidationChange={setAssetAllocationValid}
      />
    );
    const historicalValuesTab = (
      <HistoricalValuesTab
        ref={historicalValuesRef}
        account={account}
        modalOpen={open}
        onChange={setHistoricalChanges}
        onValidationChange={setHistoricalValid}
      />
    );

    const allTabs = [
      { label: 'Information', component: informationTab },
      { label: 'Asset Allocation', component: assetAllocationTab },
      { label: 'Historical Values', component: historicalValuesTab },
    ];

    return account.AccountType === 2 ? allTabs : [allTabs[0]];
  }, [
    account,
    open,
    onClose,
    setInformationValid,
    setAssetAllocationValid,
    setHistoricalValid,
    setHistoricalChanges,
  ]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCancel = () => {
    if (isSaving) return;
    if (hasUnsavedChanges()) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setDiscardConfirmOpen(false);
    onClose();
  };

  const handleSaveFromConfirm = async () => {
    setDiscardConfirmOpen(false);
    try {
      await handleSave();
      onClose();
    } catch {
      // handleSave already logs; modal stays open so user can fix and retry
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const isOffline = account.AccountType === 2;
      if (isOffline) {
        // Single combined PUT so Information, Asset Allocation, and Historical Values are all persisted
        const infoPayload = await infoRef.current?.getPayload();
        const assetPayload =
          (assetRef.current?.hasChanges() ?? false)
            ? assetRef.current?.getPayload()
            : undefined;
        const histPayload = await historicalValuesRef.current?.getPayload();
        if (!infoPayload) {
          throw new Error('Information tab payload is required');
        }
        const merged = {
          ...infoPayload,
          ...(assetPayload ?? {}),
          ...(histPayload ?? {}),
          AccountId: account.AccountId,
          AccountType: account.AccountType,
        } as OfflineAccount;

        await updateOfflineAccount(merged).unwrap();
        await infoRef.current?.syncTagMembership();
        dispatch(
          diversificationsApi.util.invalidateTags([
            { type: 'Diversification', id: 'LIST' },
          ]),
        );
      } else {
        // Online: only Information tab applies
        if (infoRef.current) {
          await infoRef.current.submit();
        }
      }

      dispatch(
        accountsApi.util.invalidateTags([{ type: 'Accounts', id: 'LIST' }]),
      );
      onClose();
    } catch (err) {
      console.error('Error saving account:', err);
      // Keep modal open on error so user can try again
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isSaving ? undefined : handleCancel}
        fullWidth
        maxWidth='md'
        fullScreen={isMobile}
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: {
            '& .MuiPaper-root': {
              paddingTop:
                'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
              paddingBottom:
                'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
            },
          },
        })}
      >
        {isSaving && (
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: (theme) => theme.zIndex.modal + 1,
              position: 'absolute',
              borderRadius: 1,
            }}
            open
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress
                color='inherit'
                size={60}
              />
              <Typography variant='h6'>Saving changes...</Typography>
            </Box>
          </Backdrop>
        )}
        <DialogTitle
          component='div'
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant='h6'
            component='h2'
            sx={{ fontSize: '1.25rem', fontWeight: 600, flex: 1 }}
          >
            Edit {account.Name}
          </Typography>
          <IconButton
            onClick={handleCancel}
            aria-label='close'
            disabled={isSaving}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              marginTop: 2,
              paddingBottom: 2,
            }}
          >
            <StyledTabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label='edit account tabs'
            >
              {tabs.map((tab, index) => (
                <StyledTab
                  key={index}
                  label={tab.label}
                />
              ))}
            </StyledTabs>
          </Box>
          {tabs.map((tab, index) => (
            <TabPanel
              key={index}
              value={tabValue}
              index={index}
            >
              {tab.component}
            </TabPanel>
          ))}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            justifyContent: 'flex-end',
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <StyledButton
            size='large'
            variant='outlined'
            onClick={handleCancel}
            sx={{ mr: 1 }}
            disabled={isSaving}
          >
            Cancel
          </StyledButton>
          <StyledButton
            size='large'
            variant='contained'
            onClick={() => {
              void handleSave();
            }}
            disabled={
              isSaving ||
              (account.AccountType === 2
                ? !isInformationValid ||
                  !isAssetAllocationValid ||
                  !isHistoricalValid
                : !isInformationValid)
            }
            startIcon={
              isSaving ? (
                <CircularProgress
                  size={20}
                  color='inherit'
                />
              ) : undefined
            }
          >
            {isSaving ? 'Saving...' : 'Save'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={discardConfirmOpen}
        onClose={() => {
          setDiscardConfirmOpen(false);
        }}
        maxWidth='sm'
        fullWidth
        aria-labelledby='unsaved-changes-dialog-title'
        aria-describedby='unsaved-changes-dialog-description'
      >
        <DialogTitle
          id='unsaved-changes-dialog-title'
          component='div'
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 6,
          }}
        >
          <Typography
            variant='h6'
            component='h2'
            sx={{ flex: 1 }}
          >
            {t('editAccountModal.discardConfirmTitle')}
          </Typography>
          <IconButton
            aria-label='close'
            onClick={() => {
              setDiscardConfirmOpen(false);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography id='unsaved-changes-dialog-description'>
            {t('editAccountModal.discardConfirmMessage')}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size='large'
              variant='outlined'
              onClick={() => {
                setDiscardConfirmOpen(false);
              }}
            >
              {t('editAccountModal.keepEditing')}
            </Button>
            <Button
              size='large'
              variant='outlined'
              color='error'
              onClick={handleDiscardConfirm}
            >
              {t('editAccountModal.discardChanges')}
            </Button>
          </Box>
          <Button
            size='large'
            variant='contained'
            onClick={() => {
              void handleSaveFromConfirm();
            }}
            disabled={isSaving}
          >
            {isSaving ? (
              <CircularProgress size={24} />
            ) : (
              t('editAccountModal.saveChanges')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditAccountModal;
